import React, { useState } from 'react';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

interface SignupFormData {
  code: string;
  company_name: string;
  company_slug: string;
  contact_email: string;
  contact_phone: string;
  admin_username: string;
  admin_password: string;
  admin_password_confirm: string;
  admin_email: string;
  subscription_tier: string;
}

const Signup: React.FC = () => {
  const [step, setStep] = useState<'code' | 'captcha' | 'form' | 'success'>('code');
  const [code, setCode] = useState('');
  const [codeValid, setCodeValid] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [formData, setFormData] = useState<SignupFormData>({
    code: '',
    company_name: '',
    company_slug: '',
    contact_email: '',
    contact_phone: '',
    admin_username: '',
    admin_password: '',
    admin_password_confirm: '',
    admin_email: '',
    subscription_tier: 'starter'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState('');

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.toUpperCase().trim());
    setCodeError('');
  };

  const validateCode = async () => {
    if (!code.trim()) {
      setCodeError('Please enter your early access code');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/signup/validate-code`, {
        code: code.trim()
      });

      if (response.data.valid) {
        setCodeValid(true);
        setFormData(prev => ({ ...prev, code: code.trim() }));
        setStep('captcha');
      } else {
        setCodeError(response.data.message || 'Invalid early access code');
      }
    } catch (error: any) {
      setCodeError(error.response?.data?.detail || 'Failed to validate code. Please try again.');
    }
  };

  const handleCaptchaSuccess = (token: string) => {
    setCaptchaToken(token);
    setStep('form');
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.admin_username.trim()) {
      newErrors.admin_username = 'Admin username is required';
    } else if (formData.admin_username.length < 3) {
      newErrors.admin_username = 'Username must be at least 3 characters';
    }

    if (!formData.admin_password) {
      newErrors.admin_password = 'Password is required';
    } else if (formData.admin_password.length < 8) {
      newErrors.admin_password = 'Password must be at least 8 characters';
    }

    if (formData.admin_password !== formData.admin_password_confirm) {
      newErrors.admin_password_confirm = 'Passwords do not match';
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }

    if (formData.admin_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      newErrors.admin_email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    if (!captchaToken) {
      setSubmitError('Please complete the CAPTCHA verification');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        code: formData.code,
        captcha_token: captchaToken,
        company_name: formData.company_name.trim(),
        company_slug: formData.company_slug.trim() || undefined,
        contact_email: formData.contact_email.trim() || undefined,
        contact_phone: formData.contact_phone.trim() || undefined,
        admin_username: formData.admin_username.trim(),
        admin_password: formData.admin_password,
        admin_email: formData.admin_email.trim() || undefined,
        subscription_tier: formData.subscription_tier
      };

      const response = await axios.post(`${API_URL}/signup/register`, payload);

      if (response.data.success) {
        setRedirectUrl(response.data.redirect_url);
        setStep('success');
        
        // Redirect after 2 seconds
        setTimeout(() => {
          window.location.href = response.data.redirect_url;
        }, 2000);
      }
    } catch (error: any) {
      setSubmitError(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* RackPlane Branding Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#007BFF] mb-2">RackPlane</h1>
          <p className="text-gray-600 text-sm">Datacenter Inventory Management</p>
        </div>
        
        <div>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Create Your Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Get started with early access
          </p>
        </div>

        {step === 'code' && (
          <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
            <form onSubmit={(e) => { e.preventDefault(); validateCode(); }} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Early Access Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  required
                  value={code}
                  onChange={handleCodeChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] focus:z-10 sm:text-sm"
                  placeholder="Enter your early access code"
                />
                {codeError && (
                  <p className="mt-2 text-sm text-red-600">{codeError}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007BFF] hover:bg-[#0056b3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF] transition-colors"
              >
                Continue
              </button>
            </form>
          </div>
        )}

        {step === 'captcha' && (
          <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
            <CaptchaComponent onSuccess={handleCaptchaSuccess} />
          </div>
        )}

        {step === 'form' && (
          <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                  Company Name *
                </label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={handleFormChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] sm:text-sm"
                />
                {errors.company_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.company_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="admin_username" className="block text-sm font-medium text-gray-700">
                  Admin Username *
                </label>
                <input
                  id="admin_username"
                  name="admin_username"
                  type="text"
                  required
                  value={formData.admin_username}
                  onChange={handleFormChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] sm:text-sm"
                />
                {errors.admin_username && (
                  <p className="mt-1 text-sm text-red-600">{errors.admin_username}</p>
                )}
              </div>

              <div>
                <label htmlFor="admin_password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <input
                  id="admin_password"
                  name="admin_password"
                  type="password"
                  required
                  value={formData.admin_password}
                  onChange={handleFormChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] sm:text-sm"
                />
                {errors.admin_password && (
                  <p className="mt-1 text-sm text-red-600">{errors.admin_password}</p>
                )}
              </div>

              <div>
                <label htmlFor="admin_password_confirm" className="block text-sm font-medium text-gray-700">
                  Confirm Password *
                </label>
                <input
                  id="admin_password_confirm"
                  name="admin_password_confirm"
                  type="password"
                  required
                  value={formData.admin_password_confirm}
                  onChange={handleFormChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] sm:text-sm"
                />
                {errors.admin_password_confirm && (
                  <p className="mt-1 text-sm text-red-600">{errors.admin_password_confirm}</p>
                )}
              </div>

              <div>
                <label htmlFor="admin_email" className="block text-sm font-medium text-gray-700">
                  Admin Email
                </label>
                <input
                  id="admin_email"
                  name="admin_email"
                  type="email"
                  value={formData.admin_email}
                  onChange={handleFormChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#007BFF] focus:border-[#007BFF] sm:text-sm"
                />
                {errors.admin_email && (
                  <p className="mt-1 text-sm text-red-600">{errors.admin_email}</p>
                )}
              </div>

              {submitError && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007BFF] hover:bg-[#0056b3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF] disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200 text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Account Created Successfully!</h3>
            <p className="text-sm text-gray-600 mb-4">Redirecting you to your workspace...</p>
            <a
              href={redirectUrl}
              className="text-[#007BFF] hover:text-[#0056b3] text-sm font-medium transition-colors"
            >
              Click here if you're not redirected automatically
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// Cloudflare Turnstile CAPTCHA component
const CaptchaComponent: React.FC<{ onSuccess: (token: string) => void }> = ({ onSuccess }) => {
  const [token, setToken] = useState<string | null>(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

  const handleVerify = (turnstileToken: string) => {
    setToken(turnstileToken);
    onSuccess(turnstileToken);
  };

  const handleError = () => {
    console.error('Turnstile verification failed');
    setToken(null);
  };

  const handleExpire = () => {
    console.warn('Turnstile token expired');
    setToken(null);
  };

  if (!turnstileSiteKey) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600 text-center">
          Turnstile site key not configured. Please set REACT_APP_TURNSTILE_SITE_KEY.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 text-center">Please verify you're not a robot</p>
      <div className="flex justify-center">
        <Turnstile
          siteKey={turnstileSiteKey}
          onSuccess={handleVerify}
          onError={handleError}
          onExpire={handleExpire}
          options={{
            theme: 'light',
            size: 'normal'
          }}
        />
      </div>
      {token && (
        <p className="text-xs text-green-600 text-center">✓ Verification complete</p>
      )}
      <p className="text-xs text-gray-500 text-center">Protected by Cloudflare Turnstile</p>
    </div>
  );
};

export default Signup;

